// Imports for useApi hook
import useUserStore from './stores/useUserStore';
import config from './config';

/**
 * In-memory token storage (cleared on page refresh)
 * Access tokens are short-lived, so storing in memory is secure
 */
let accessToken = null;
let tokenExpiresAt = null; // Track expiry for proactive refresh

/**
 * Refresh lock - prevents race condition when multiple 401s trigger concurrent refreshes
 * Without this, the first refresh rotates the token, and subsequent refreshes fail
 * because they're using the now-revoked old token.
 */
let refreshPromise = null;

/**
 * Decode JWT payload without verification (just to read expiry)
 * Safe because we only use this for timing, not for auth decisions
 */
const decodeTokenExpiry = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
  } catch {
    return null;
  }
};

export const setAccessToken = (token) => {
  accessToken = token;
  tokenExpiresAt = token ? decodeTokenExpiry(token) : null;
};

/**
 * Pluggable token source. Shell mode (the scanner kiosk) installs one so tokens
 * come from the shell and a 401 never reaches /login/refresh. Shape:
 * { getToken(): string|null, handleUnauthorized(sentToken): boolean }.
 */
let tokenProvider = null;

export const setTokenProvider = (provider) => {
  tokenProvider = provider || null;
};

export const getAccessToken = () => {
  return tokenProvider ? tokenProvider.getToken() : accessToken;
};

export const clearAccessToken = () => {
  accessToken = null;
  tokenExpiresAt = null;
};

/**
 * Check if token will expire soon (within threshold)
 * Pro apps refresh proactively to avoid failed requests
 */
const isTokenExpiringSoon = (thresholdMs = 60000) => { // 1 minute default
  if (!tokenExpiresAt) return true; // No expiry info, assume expired
  return Date.now() > (tokenExpiresAt - thresholdMs);
};

/**
 * Refresh access token using refresh token (in httpOnly cookie)
 * Uses a lock to prevent race conditions when multiple requests fail simultaneously
 */
const refreshAccessToken = async () => {
  // If a refresh is already in progress, wait for it instead of starting another
  if (refreshPromise) {
    return refreshPromise;
  }

  // Start a new refresh and store the promise
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${config.API_ENDPOINT}/login/refresh`, {
        method: 'POST',
        credentials: 'include', // Send refresh token cookie
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // Auth failure (401/403) - token is invalid, user must re-login
        const error = new Error('Refresh failed');
        error.isAuthError = true;
        error.status = response.status;
        throw error;
      }

      const data = await response.json();
      setAccessToken(data.accessToken);
      return data;
    } catch (error) {
      // Distinguish between auth errors and network errors
      if (error.isAuthError) {
        // Auth error - clear token, user needs to login again
        clearAccessToken();
      }
      // Network errors: DON'T clear token - might just be temporary connectivity issue
      // The existing token might still be valid when network recovers
      throw error;
    } finally {
      // Clear the lock so future refreshes can proceed
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Ultimate authenticated API fetch helper with JWT support
 * Handles automatic token refresh on 401 errors
 *
 * ⚠️ PREFER useApi() HOOK IN COMPONENTS - it auto-includes logout handling!
 *
 * @param {string} url - The API endpoint (relative like '/fleet' or full URL)
 * @param {Object} options - Extended fetch + UX options
 * @param {string} [options.method='GET'] - HTTP method
 * @param {Object} [options.headers] - Custom headers (will be merged with defaults)
 * @param {any} [options.body] - Request body (will auto-stringify if object)
 * @param {Function} [options.onUnauthorized] - Callback for 401 (e.g., logout)
 * @param {Function} [options.onSuccess] - Callback after successful request
 * @param {Function} [options.onError] - Callback on error (receives error object)
 * @param {string|boolean} [options.successAlert] - Show alert on success (string for custom message, true for default)
 * @param {string|boolean} [options.errorAlert] - Show alert on error (string for custom message, true for default)
 * @param {boolean} [options.skipJsonParse=false] - Return raw Response instead of parsed JSON
 * @param {boolean} [options.silent=false] - Suppress console error logging
 * @param {boolean} [options._isRetry=false] - Internal flag to prevent infinite refresh loops
 * @returns {Promise<any>} - Parsed JSON data (or raw Response if skipJsonParse)
 *
 * @example
 * const api = useApi();
 * const drones = await api.get('/fleet');
 *
 * IMPORTANT: relative paths must NOT start with /api — config.API_ENDPOINT
 * already ends in it (Vercel proxy '/api' in prod, 'localhost:5050/api' in
 * dev), so '/api/x' becomes '/api/api/x' and 404s everywhere.
 */
export const apiFetchAuthed = async (url, options = {}) => {
  // Extract our custom UX options
  const {
    onUnauthorized,
    onSuccess,
    onError,
    successAlert,
    errorAlert,
    skipJsonParse = false,
    silent = false,
    body,
    headers,
    _isRetry = false,
    ...fetchOptions
  } = options;

  try {
    // PROACTIVE REFRESH: If token expires within 1 minute, refresh NOW
    // This prevents failed requests - pro apps don't wait for 401
    if (!tokenProvider && accessToken && isTokenExpiringSoon() && !_isRetry) {
      try {
        await refreshAccessToken();
      } catch (refreshError) {
        // Proactive refresh failed - continue with current token
        // If it's truly expired, the 401 handler below will catch it
        if (!silent) {
          console.log('Proactive refresh failed, continuing with current token');
        }
      }
    }

    // Get current access token (may have been updated by proactive refresh)
    const token = getAccessToken();

    // FormData uploads must NOT be JSON-stringified and must NOT set
    // Content-Type — the browser sets the multipart boundary automatically.
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

    // Prepare the fetch request
    const requestConfig = {
      ...fetchOptions,
      credentials: 'include', // Still send cookies (for refresh token)
      headers: {
        // Default content type — skipped for FormData so the browser supplies
        // its own multipart/form-data; <boundary>.
        ...(!isFormData && { 'Content-Type': 'application/json' }),
        ...(token && { 'Authorization': `Bearer ${token}` }), // Add JWT if available
        ...headers,  // Merge user headers
      }
    };

    // Auto-stringify body if it's a plain object. FormData passes through
    // untouched so multipart parsers (multer) see real fields.
    if (body !== undefined) {
      if (isFormData || typeof body === 'string') {
        requestConfig.body = body;
      } else {
        requestConfig.body = JSON.stringify(body);
      }
    }

    // Automatically prepend base URL for relative paths
    const fullUrl = url.startsWith('http') ? url : `${config.API_ENDPOINT}${url}`;

    // Make the request
    const response = await fetch(fullUrl, requestConfig);

    // Shell mode: the provider decides whether another try could help. No refresh
    // cookie exists there, so /login/refresh would only fail and log the kiosk out.
    if (response.status === 401 && tokenProvider) {
      if (!_isRetry && tokenProvider.handleUnauthorized(token)) {
        return await apiFetchAuthed(url, { ...options, _isRetry: true });
      }
      if (_isRetry) tokenProvider.handleUnauthorized(token);
      const error = new Error('Session expired');
      error.status = 401;
      throw error;
    }

    // Handle 401 Unauthorized - Try to refresh token
    if (response.status === 401 && !_isRetry) {
      try {
        // Attempt to refresh the access token
        await refreshAccessToken();

        // Retry the original request with new token
        return await apiFetchAuthed(url, { ...options, _isRetry: true });
      } catch (refreshError) {
        // Only logout on actual AUTH errors, not network errors
        // Network errors might be temporary - don't kick user out unnecessarily
        if (refreshError.isAuthError && onUnauthorized) {
          onUnauthorized();
        }
        const error = new Error(
          refreshError.isAuthError
            ? 'Session expired - please login again'
            : 'Network error - please check your connection'
        );
        error.status = refreshError.isAuthError ? 401 : 0;
        error.isNetworkError = !refreshError.isAuthError;
        throw error;
      }
    }

    // Handle other error statuses
    if (!response.ok) {
      // Try to extract error message from response
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let errorData = null;
      try {
        errorData = await response.json();
        errorMessage = errorData.error?.message || errorData.error || errorData.message || errorMessage;
      } catch (e) {
        // If JSON parsing fails, use default message
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = errorData;
      throw error;
    }

    // Success! Parse response
    let data = null;
    if (!skipJsonParse) {
      // Handle empty responses gracefully
      const text = await response.text();
      data = text ? JSON.parse(text) : null;
    } else {
      data = response;
    }

    // Execute success callbacks
    if (successAlert) {
      const message = typeof successAlert === 'string' ? successAlert : 'Operation successful!';
      alert(message);
    }

    if (onSuccess) {
      onSuccess(data);
    }

    return data;

  } catch (error) {
    // Log error unless silent mode
    if (!silent) {
      const fullUrl = url.startsWith('http') ? url : `${config.API_ENDPOINT}${url}`;
      console.error(`API Fetch Error [${fullUrl}]:`, error);
    }

    // Show error alert if requested
    if (errorAlert) {
      const message = typeof errorAlert === 'string'
        ? errorAlert
        : error.message || 'An error occurred. Please try again.';
      alert(message);
    }

    // Execute error callback
    if (onError) {
      onError(error);
    }

    // Re-throw for caller to handle
    throw error;
  }
};

/**
 * Ultimate DRY hook for API calls - AUTO-INCLUDES logout handling!
 * Use this in components instead of calling apiFetchAuthed directly.
 *
 * @returns {Object} API helpers with pre-configured logout
 *
 * @example
 * // ========== ULTRA-DRY API CALLS ==========
 * import { useApi } from '../apiHelpers';
 *
 * function MyComponent() {
 *   const api = useApi();
 *
 *   // Simple GET - logout handling and base URL auto-included!
 *   const drones = await api.get('/fleet');
 *
 *   // POST with body
 *   await api.post('/parts', {
 *     body: newPart,
 *     successAlert: "Part added!"
 *   });
 *
 *   // PATCH with success callback
 *   await api.patch(`/parts/${id}`, {
 *     body: updatedPart,
 *     successAlert: "Updated!",
 *     onSuccess: () => history.push('/parts')
 *   });
 *
 *   // DELETE with error handling
 *   await api.delete(`/parts/${id}`, {
 *     successAlert: "Deleted!",
 *     errorAlert: "Failed to delete"
 *   });
 * }
 */
export const useApi = () => {
  const logout = useUserStore((state) => state.logout);

  // Return convenience methods that auto-include logout
  return {
    /**
     * GET request with auto logout
     */
    get: (url, options = {}) => {
      return apiFetchAuthed(url, {
        method: 'GET',
        onUnauthorized: logout,
        ...options
      });
    },

    /**
     * POST request with auto logout
     */
    post: (url, options = {}) => {
      return apiFetchAuthed(url, {
        method: 'POST',
        onUnauthorized: logout,
        ...options
      });
    },

    /**
     * PATCH request with auto logout
     */
    patch: (url, options = {}) => {
      return apiFetchAuthed(url, {
        method: 'PATCH',
        onUnauthorized: logout,
        ...options
      });
    },

    /**
     * PUT request with auto logout
     */
    put: (url, options = {}) => {
      return apiFetchAuthed(url, {
        method: 'PUT',
        onUnauthorized: logout,
        ...options
      });
    },

    /**
     * DELETE request with auto logout
     */
    delete: (url, options = {}) => {
      return apiFetchAuthed(url, {
        method: 'DELETE',
        onUnauthorized: logout,
        ...options
      });
    },

    /**
     * Custom request (full control)
     * Still auto-includes logout
     */
    fetch: (url, options = {}) => {
      return apiFetchAuthed(url, {
        onUnauthorized: logout,
        ...options
      });
    }
  };
};
